import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  FolderKanban,
  CheckSquare,
  Calendar,
  LayoutGrid,
} from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface ProjectWithCounts {
  id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
  task_count: number;
  meeting_series_count: number;
  reference_doc_count: number;
}

const PRESET_COLORS = [
  '#6366f1',
  '#ec4899',
  '#f59e0b',
  '#22c55e',
  '#06b6d4',
  '#8b5cf6',
  '#ef4444',
  '#64748b',
];

export function ProjectsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [projects, setProjects] = useState<ProjectWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectWithCounts | null>(null);

  async function load() {
    try {
      setProjects(await apiGet<ProjectWithCounts[]>('/scopes'));
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(name: string, description: string, color: string) {
    try {
      await apiPost('/scopes', { name, description: description || null, color });
      setShowCreate(false);
      toast.success('Project created');
      load();
    } catch (err) {
      console.error('Failed to create project:', err);
      toast.error('Failed to create project');
    }
  }

  async function handleUpdate(
    id: string,
    name: string,
    description: string,
    color: string
  ) {
    try {
      await apiPatch(`/scopes/${id}`, { name, description: description || null, color });
      setEditingProject(null);
      toast.success('Project updated');
      load();
    } catch (err) {
      console.error('Failed to update project:', err);
      toast.error('Failed to update project');
    }
  }

  async function handleDelete(id: string) {
    if (
      !window.confirm(
        'Delete this project? It will be unlinked from all tasks, meetings, and documents.'
      )
    )
      return;
    try {
      await apiDelete(`/scopes/${id}`);
      toast.success('Project deleted');
      load();
    } catch (err) {
      console.error('Failed to delete project:', err);
      toast.error('Failed to delete project');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Projects</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <FolderKanban size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No projects yet</p>
          <p className="text-sm">
            Projects help you group tasks, meetings, and documents together.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-bg-card border border-border rounded-lg p-4 hover:border-border-hover transition-colors cursor-pointer group relative"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              {/* Edit/Delete controls */}
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingProject(project);
                  }}
                  className="p-1 text-text-muted hover:text-text-primary rounded hover:bg-bg-hover transition-colors"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(project.id);
                  }}
                  className="p-1 text-text-muted hover:text-danger rounded hover:bg-bg-hover transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Project name + color */}
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: project.color }}
                />
                <span className="text-sm font-semibold text-text-primary truncate">
                  {project.name}
                </span>
              </div>

              {/* Description */}
              {project.description && (
                <p className="text-xs text-text-muted mb-3 line-clamp-2">
                  {project.description}
                </p>
              )}

              {/* Counts */}
              <div className="flex items-center gap-4 text-xs text-text-muted mt-auto pt-2 border-t border-border/50">
                <span className="flex items-center gap-1">
                  <CheckSquare size={12} /> {project.task_count} task
                  {project.task_count !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={12} /> {project.meeting_series_count} meeting
                  {project.meeting_series_count !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <LayoutGrid size={12} /> {project.reference_doc_count} view
                  {project.reference_doc_count !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreate || editingProject) && (
        <ProjectModal
          project={editingProject}
          onSave={(name, description, color) =>
            editingProject
              ? handleUpdate(editingProject.id, name, description, color)
              : handleCreate(name, description, color)
          }
          onClose={() => {
            setShowCreate(false);
            setEditingProject(null);
          }}
        />
      )}
    </div>
    </div>
  );
}

function ProjectModal({
  project,
  onSave,
  onClose,
}: {
  project: ProjectWithCounts | null;
  onSave: (name: string, description: string, color: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [color, setColor] = useState(project?.color ?? '#6366f1');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-text-primary mb-4">
          {project ? 'Edit Project' : 'New Project'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g., Q1 Launch"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this project about?"
              rows={3}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Color</label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    color === c ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onSave(name.trim(), description.trim(), color)}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            {project ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
